import { Dialog } from '@material-ui/core';
import { isArray } from 'lodash';
import React from 'react';

import { ReactComponent as IconClose } from 'src/assets/svgs/icon-close.svg';

import { IntakeForm } from './CareFormSection';

const questions = [
  {
    text: 'What has motivated you to seek coaching?',
    questionName: 'MOTIVATION_TO_SEEK_COACHING',
  },
  {
    text: 'Have you experienced coaching and/or therapy before?',
    questionName: 'EXPERIENCED_COACHING_BEFORE',
  },
  {
    text: 'How would you describe your personal and professional life right now?',
    questionName: 'CURRENT_PERSONAL_AND_PROFESSIONAL_LIFE',
  },
  {
    text: 'How can I support you in coaching? Rate the following (Rank: 1-5).',
    questionName: '',
    nestedQuestions: [
      {
        text: 'Accountability',
        questionName: 'RANK_SUPPORT_ACCOUNTABILITY',
      },
      {
        text: 'Brainstorming',
        questionName: 'RANK_SUPPORT_BRAINSTORMING',
      },
      {
        text: 'Exploring and removing obstacles',
        questionName: 'RANK_SUPPORT_EXPLORING_OBSTACLES',
      },
      {
        text: 'Insight into your potential',
        questionName: 'RANK_SUPPORT_INSIGHT_INTO_POTENTIAL',
      },
      {
        text: 'Vision',
        questionName: 'RANK_SUPPORT_VISION',
      },
      {
        text: 'Encouragement and inspiration',
        questionName: 'RANK_SUPPORT_ENCOURAGEMENT',
      },
    ],
  },
  {
    text: 'What are the top 2-3 specific outcomes that you want to achieve from coaching?',
    questionName: 'OUTCOMES_TO_ACHIEVE',
  },
  {
    text: 'At the end of your sessions, how would you like to describe your personal and professional life?',
    questionName: 'POST_SESSIONS_PERSONAL_AND_PROFESSIONAL_LIFE',
  },
  {
    text: 'Anything else on your mind?',
    questionName: 'ANYTHING_ELSE',
  },
];

const experiencedCoachingTherapyBeforeQuestionReadableAnswers = {
  YES_EXPERIENCED_THERAPY: 'Yes, I have experienced therapy',
  YES_EXPERIENCED_COACHING: 'Yes, I have experienced coaching',
  NO_EXPERIENCE: 'No, I do not have experience with coaching or therapy',
};

export enum CareFormModalTestID {
  CloseButton = 'CareFormModalTestID.CloseButton',
  ModalVisible = 'CareFormModalTestID.ModalVisible',
}

interface Props {
  intakeForm: IntakeForm;
  onClose: () => void;
  visible: boolean;
}

export const CareFormModal: React.FC<Props> = ({ intakeForm, visible, onClose }) => {
  const questionNameAnswerMap = intakeForm?.intakeFormQuestionResponses.reduce((acc, current) => {
    // merging OUTCOMES_TO_ACHIEVE_FIRST, OUTCOMES_TO_ACHIEVE_SECOND, OUTCOMES_TO_ACHIEVE_THIRD into OUTCOMES_TO_ACHIEVE
    if (current.questionName.includes('OUTCOMES_TO_ACHIEVE_') && current.questionResponseContent) {
      const outcome = acc['OUTCOMES_TO_ACHIEVE'];
      return {
        ...acc,
        OUTCOMES_TO_ACHIEVE: outcome
          ? `${outcome} \n ${current.questionResponseContent}`
          : current.questionResponseContent,
      };
    }

    // replacing non-readable answers with a readable ones and merging an array into one string
    if (
      current.questionName === 'EXPERIENCED_COACHING_BEFORE' &&
      isArray(current?.questionResponseContent)
    ) {
      return {
        ...acc,
        [current.questionName]: current.questionResponseContent
          .map((response) => experiencedCoachingTherapyBeforeQuestionReadableAnswers[response])
          .join('\n'),
      };
    }

    return {
      ...acc,
      [current.questionName]: current.questionResponseContent,
    };
  }, {});

  const displayQuestionAnswer = (question, defaultAnswer) => {
    if (!intakeForm.submitted) {
      return '-';
    }

    return questionNameAnswerMap[question.questionName] || defaultAnswer;
  };

  return (
    <Dialog
      open={visible}
      onClose={onClose}
      maxWidth="lg"
      data-testid={visible && CareFormModalTestID.ModalVisible}
    >
      <div className="py-8 px-12 h-full max-w-3xl relative">
        <button
          data-testid={CareFormModalTestID.CloseButton}
          onClick={onClose}
          className="bg-white cursor-pointer rounded-full h-8 w-8 absolute top-4 right-4 flex justify-center items-center border-2 border-gray-250"
        >
          <IconClose className="w-2.5 h-2.5 text-gray-850 fill-current stroke-current" />
        </button>
        <div className="flex flex-col">
          <h4 className="text-gray-900 font-extrabold text-xl">
            {`Care Form - ${intakeForm?.completedBy?.firstName} ${intakeForm?.completedBy?.lastName}`}
          </h4>
          <ul className="mt-2">
            {questions.map((question, i) => (
              <li key={question.questionName} className="mb-5">
                <p className="text-gray-900 font-extrabold text-sm mt-4">{`${i + 1}. ${
                  question.text
                }`}</p>
                {question.questionName ? (
                  <p className="text-gray-600 font-normal text-sm mt-3 whitespace-pre-line">
                    {displayQuestionAnswer(question, 'Your client skipped this question.')}
                  </p>
                ) : null}

                {question?.nestedQuestions ? (
                  <ul>
                    {question?.nestedQuestions.map((nestedQuestion) => (
                      <li
                        className="flex justify-between mt-3 items-center"
                        key={nestedQuestion.questionName}
                      >
                        <p className="text-gray-600 font-normal text-sm">{nestedQuestion.text}</p>
                        <div className="w-auto flex-1 border-dashed border-b-2 border-gray-400 border-opacity-80 mx-3 h-px" />
                        <p className="text-gray-600 font-normal text-sm border-2 px-4 py-1 rounded">
                          {displayQuestionAnswer(nestedQuestion, '-')}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Dialog>
  );
};
